import React, { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Box, Card } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { MRT_ColumnDef, MRT_PaginationState, MRT_Row } from 'material-react-table';

import { mapBoolean, MaterialTable } from 'commonComponents/MaterialTable';
import { PageContainer } from 'commonComponents/PageContainer';

import { loadUserList, type UserDetails } from 'api/users';
import { ROUTES } from 'routes/routes';

import { UserListButtons } from './UserListButtons';

let columns: Array<MRT_ColumnDef<UserDetails>> = [
    { header: 'User Name', accessorKey: 'username' },
    { header: 'Full Name', accessorKey: 'full_name' },
    { header: 'Email Address', accessorKey: 'email' },
    { header: 'Role', accessorKey: 'userType' },
    { header: 'Internal', accessorFn: user => mapBoolean(user.is_internal) },
    { header: 'Active', accessorFn: user => mapBoolean(user.is_active) },
];
columns = columns.map(c => ({ ...c, id: c.header }));

export function UserList() {
    const navigate = useNavigate();
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 });
    const { data: users, isInitialLoading } = useQuery({
        queryKey: ['User', 'list'],
        queryFn: loadUserList,
        select: users => users.map(u => ({ ...u, strikethrough: u.invalidated })),
        meta: { errorMessage: 'Loading users failed' },
    });

    const defaultSort = [{ id: 'User Name', desc: false }];
    const showDetails = (row: MRT_Row<UserDetails>) =>
        navigate(generatePath(ROUTES.USER_DETAIL, { id: row.original.id.toString() }));

    return (
        <PageContainer dataTestId="UserList_Container" maxWidth={false} maxHeight="100%">
            <Card sx={{ padding: 1, marginBottom: 1, overflow: 'inherit' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                >
                    <UserListButtons />
                </Box>
            </Card>
            <MaterialTable
                name="users"
                data={users || []}
                columns={columns}
                defaultSort={defaultSort}
                onRowClicked={showDetails}
                pagination={pagination}
                setPagination={setPagination}
                isInitialLoading={isInitialLoading}
            />
        </PageContainer>
    );
}
